import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, Lock, Network, Radio, ArrowUpRight, Lightbulb, Eye, Wrench, Users, Mail, Bell } from "lucide-react";
import { useState } from "react";
import { NewsletterForm } from "@/components/NewsletterForm";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { SparkleDivider } from "@/components/SparkleDivider";
import scottAvatar from "@/assets/scott-avatar.webp";

const DESCRIPTION =
  "Monthly field notes from Scott Cunningham (@scottcbusiness) on AI, Web3, privacy, social media, and the systems shaping the internet.";

export const Route = createFileRoute("/newsletter")({
  head: () => ({
    meta: [
      { title: "Newsletter | The Digital Debrief by Scott Cunningham" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "The Digital Debrief Newsletter" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://scottcbusiness.com/newsletter" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "The Digital Debrief Newsletter" },
      { name: "twitter:description", content: DESCRIPTION },
      { property: "og:image", content: "https://scottcbusiness.com/og-newsletter.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "The Digital Debrief: field notes from the edges of the internet",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image:alt", content: "The Digital Debrief: field notes from the edges of the internet" },
      { name: "twitter:image", content: "https://scottcbusiness.com/og-newsletter.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/newsletter" }],
  }),
  component: NewsletterPage,
});

const pillars = [
  {
    icon: Sparkles,
    title: "AI, in practice",
    body: "Where it actually helps, where it's hype, and how I'm using it in my own workflow.",
    emailBody:
      "Hey there,\n\nThis issue covers where AI is actually saving time right now, where it's still mostly noise, and the specific workflows I am testing in content and social operations. Expect practical notes, a few honest failures, and one tactic you can try before the next debrief.",
    accent: "from-orange-400 to-pink-500",
    dot: "bg-orange-400",
    issue: "Issue 01",
  },
  {
    icon: Network,
    title: "Web3 & decentralization",
    body: "Protocols, incentives, and the slow rebuild of the internet's underlying rails.",
    emailBody:
      "Hey there,\n\nThis issue looks at the protocols and incentives behind decentralization, which shifts are actually sticking, and what they mean for how content and communities are distributed. I'll also flag the one signal worth watching this month.",
    accent: "from-pink-500 to-fuchsia-500",
    dot: "bg-pink-500",
    issue: "Issue 02",
  },
  {
    icon: Lock,
    title: "Privacy & sovereignty",
    body: "Tools, tactics, and trade-offs for owning more of your digital footprint.",
    emailBody:
      "Hey there,\n\nThis issue is about privacy tools, digital sovereignty trade-offs, and the practical steps creators and marketers can take to own more of their stack. I'll share what's working, what's overhyped, and where to start without overwhelming yourself.",
    accent: "from-fuchsia-500 to-violet-500",
    dot: "bg-violet-500",
    issue: "Issue 03",
  },
  {
    icon: Radio,
    title: "Social & platform systems",
    body: "How the algorithms, business models, and policies behind your feed actually work.",
    emailBody:
      "Hey there,\n\nThis issue breaks down the algorithms, business models, and policy changes shaping social platforms, and what they mean for anyone building an audience. I'll translate the platform noise into something you can actually plan around.",
    accent: "from-violet-500 to-sky-500",
    dot: "bg-sky-500",
    issue: "Issue 04",
  },
];

const whatReadersReceive = [
  {
    icon: Lightbulb,
    title: "One original argument",
    body: "A clear take or observation I haven't published on the blog yet. Something to consider, argue with, or act on.",
    label: "The argument",
  },
  {
    icon: Eye,
    title: "Developments worth watching",
    body: "The 2-3 shifts in AI, Web3, privacy, or platform policy that actually matter this month.",
    label: "The signal",
  },
  {
    icon: Wrench,
    title: "A tool or system to test",
    body: "One platform, workflow, or tactic I am trying, with notes on where it fits and where it falls short.",
    label: "The toolkit",
  },
  {
    icon: Users,
    title: "What it means for you",
    body: "Practical implications for marketers, builders, and creators navigating the same landscape.",
    label: "The takeaway",
  },
];

function NewsletterPage() {
  return (
    <>
      {/* Hero + author bio */}
      <section className="relative isolate overflow-hidden border-b border-border grain">
        <LightSectionBackdrop variant="abstract" />
        <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
        <div
          className="blob float-slow right-[-120px] top-40 h-[360px] w-[360px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-20">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em]">
            <span className="text-gradient">The Digital Debrief</span>
          </p>
          <h1 className="mt-5 font-display text-4xl leading-[1.05] md:text-5xl lg:text-6xl text-balance">
            Field notes from the <em className="italic text-gradient">edges</em> of the internet.
          </h1>
          <p className="mt-5 text-lg text-foreground max-w-2xl md:text-foreground/80">
            Subscribe to my monthly newsletter for practical field notes on AI, Web3, privacy,
            decentralization, social media, and the systems shaping the internet. We go further
            beyond the blog content and get into the finer details.
          </p>

          <div className="mt-6 max-w-lg">
            <NewsletterForm tone="light" />
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Monthly · No spam · Unsubscribe anytime
            </p>
          </div>

          {/* Author bio under the first part */}
          <div className="mt-8 flex items-center gap-4 border-t border-border/50 pt-6">
            <img
              src={scottAvatar}
              alt="Scott Cunningham"
              width={56}
              height={56}
              className="h-14 w-14 rounded-full border border-foreground/20 object-cover shadow-pop"
              loading="lazy"
            />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Written by
              </p>
              <p className="font-display text-lg leading-tight">Scott Cunningham</p>
              <p className="max-w-lg text-sm text-foreground/70">
                Content & social lead at the intersection of AI, Web3, and privacy.
              </p>
              <Link
                to="/about"
                className="mt-1 inline-flex items-center gap-1 border-b border-foreground/30 pb-0.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-foreground"
              >
                More about Scott <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What readers actually receive */}
      <section className="relative isolate overflow-hidden border-b border-border bg-background">
        <LightSectionBackdrop variant="blueprint" />
        <div className="relative mx-auto max-w-5xl px-6 py-8 lg:py-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            What readers actually receive
          </p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl text-balance max-w-2xl">
            Each issue picks one thread, then <em className="italic text-gradient">translates it</em> into something you can use.
          </h2>

          {/* Compact zigzag of floating email cards */}
          <ol className="relative mt-14 flex flex-col sm:gap-0 float-zigzag">
            {whatReadersReceive.map(({ icon: Icon, title, body, label }, i) => {
              const isRight = i % 2 === 1;
              const alignClass = isRight ? "sm:ml-auto sm:mr-0" : "sm:mr-auto sm:ml-0";
              return (
                <li
                  key={title}
                  className="relative mb-4 sm:mb-6 last:mb-0 flex flex-col items-stretch sm:block"
                >
                  {/* Email-style card with gradient border glow on hover */}
                  <div
                    className={`group relative w-full sm:w-[45%] transition-all duration-300 hover:-translate-y-1.5 hover:scale-[1.2] ${alignClass}`}
                  >
                    {/* Gradient glow ring */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -inset-[3px] rounded-t-md rounded-b-lg bg-gradient-to-br from-orange-400 via-pink-500 to-violet-500 opacity-0 blur-[3px] transition-opacity duration-300 group-hover:opacity-100"
                    />
                    <div className="relative overflow-hidden rounded-t-md rounded-b-lg border border-border/70 bg-background shadow-card">
                      {/* Envelope header bar */}
                      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {String(i + 1).padStart(2, "0")} · {label}
                        </span>
                      </div>
                      <div className="flex items-start gap-3 p-3.5">
                        <div
                          className="flex h-9 w-9 flex-none items-center justify-center rounded-md text-white bg-gradient-to-br from-orange-400 via-pink-500 to-violet-500"
                        >
                          <Icon className="h-4.5 w-4.5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-display text-base sm:text-lg text-foreground">{title}</h3>
                          <p className="mt-1 text-sm text-muted-foreground leading-snug">{body}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tight spiral connector behind the cards */}
                  {i < whatReadersReceive.length - 1 && (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute -z-10 inset-x-0 top-[calc(100%-1rem)] h-16 hidden sm:block"
                    >
                      <svg
                        viewBox="0 0 100 40"
                        preserveAspectRatio="none"
                        className="absolute inset-0 h-full w-full overflow-visible"
                      >
                        <defs>
                          <linearGradient id={`spiral-grad-${i}`} x1="0" x2="1" y1="0" y2="1">
                            <stop offset="0%" stopColor="oklch(0.66 0.26 28)" />
                            <stop offset="50%" stopColor="oklch(0.6 0.28 305)" />
                            <stop offset="100%" stopColor="oklch(0.6 0.26 265)" />
                          </linearGradient>
                          <marker
                            id={`spiral-arrow-${i}`}
                            viewBox="0 0 10 10"
                            refX="8"
                            refY="5"
                            markerWidth="4"
                            markerHeight="4"
                            orient="auto-start-reverse"
                          >
                            <path d="M0,0 L10,5 L0,10 z" fill="oklch(0.6 0.28 305)" />
                          </marker>
                        </defs>
                        <path
                          d={
                            isRight
                              ? "M 55 0 C 55 20, 45 10, 45 40"
                              : "M 45 0 C 45 20, 55 10, 55 40"
                          }
                          fill="none"
                          stroke={`url(#spiral-grad-${i})`}
                          strokeWidth="1.5"
                          strokeDasharray="3 4"
                          strokeLinecap="round"
                          markerEnd={`url(#spiral-arrow-${i})`}
                          className="opacity-70 dash-flow"
                        />
                      </svg>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </section>


      {/* Pillars — dark background for contrast */}
      <section className="relative isolate overflow-hidden border-y border-border bg-ink text-cream">
        <DarkSectionBackdrop arcs sparkles intensity="soft" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 lg:px-10 lg:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/60">
            What you'll get
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl text-balance max-w-2xl text-cream">
            Four threads, one <em className="italic text-gradient">inbox.</em>
          </h2>
          <InboxPillars />
        </div>
      </section>


      {/* Secondary CTA */}
      <section className="relative isolate overflow-hidden bg-background">
        <LightSectionBackdrop variant="abstract" />
        <div className="relative mx-auto max-w-3xl px-6 py-14 text-center lg:px-10 lg:py-16">
          <h2 className="font-display text-3xl md:text-4xl text-balance">
            Ready <em className="italic text-gradient not-italic">when you are.</em>
          </h2>
          <p className="mt-3 text-foreground/75">
            Join readers getting the next debrief in their inbox.
          </p>
          <div className="mx-auto mt-6 grid w-full place-items-center overflow-visible px-0">
            <NewsletterForm tone="light" mode="iframe" className="newsletter-form-ready-cta" />
          </div>
        </div>
      </section>

      <SparkleDivider />
    </>
  );
}

function InboxPillars() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const current = pillars[active];
  const Icon = current.icon;

  return (
    <div className="group relative mt-8">
      {/* Outer gradient glow ring */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-[4px] rounded-[calc(1rem+4px)] bg-gradient-to-br from-orange-400 via-pink-500 to-violet-500 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100"
      />

      <div
        className={`relative overflow-hidden rounded-2xl bg-cream text-ink shadow-[0_16px_50px_-16px_rgba(0,0,0,0.6)] transition-all duration-500 ease-out group-hover:scale-[1.015] ${
          open ? "min-h-[360px] md:min-h-[480px]" : "min-h-[220px] md:min-h-[260px]"
        }`}
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        role="button"
        aria-expanded={open}
        aria-label="Open newsletter inbox preview"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {/* Teaser: "You've got mail" */}
        <div
          className={`absolute inset-0 z-20 flex flex-col items-center justify-center transition-all duration-500 ease-out ${
            open ? "pointer-events-none scale-95 opacity-0" : "scale-100 opacity-100"
          }`}
        >
          <div className="relative">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 text-white shadow-md">
              <Bell className="h-7 w-7" />
            </div>
            <span className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-cream bg-gradient-to-br from-orange-400 to-pink-500 text-[10px] font-bold text-white shadow-sm">
              {pillars.length}
            </span>
          </div>
          <h3 className="mt-5 font-display text-2xl md:text-3xl text-ink">You've got mail</h3>
          <p className="mt-1.5 text-sm text-ink/80">
            {pillars.length} unread issues from The Digital Debrief
          </p>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
            Hover or tap to open
          </p>
        </div>

        {/* Inbox UI */}
        <div
          className={`transition-all duration-500 ease-out ${
            open
              ? "relative z-10 opacity-100"
              : "pointer-events-none absolute inset-0 opacity-0"
          }`}
        >
          {/* Window chrome */}
          <div className="flex items-center gap-2 border-b border-ink/10 bg-ink/[0.04] px-4 py-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
            <span className="ml-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">
              <Mail className="h-3 w-3" /> Inbox · The Digital Debrief
            </span>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
              {pillars.length} unread
            </span>
          </div>

          <div className="grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:min-h-[480px]">
            {/* Inbox list */}
            <ul
              role="listbox"
              aria-label="Newsletter topics"
              className="border-b border-ink/10 md:border-b-0 md:border-r md:min-h-full md:flex md:flex-col"
            >
              {pillars.map((p, i) => {
                const selected = i === active;
                return (
                  <li key={p.title} className="md:flex-1 md:flex md:flex-col">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      onClick={() => setActive(i)}
                      className={`group/item relative flex w-full md:h-full items-start md:items-center gap-3 border-b border-ink/5 px-4 py-4 md:py-5 text-left transition-colors last:border-b-0 ${
                        selected ? "bg-ink/[0.06]" : "hover:bg-ink/[0.03]"
                      }`}
                    >
                      {/* Accent bar */}
                      <span
                        aria-hidden
                        className={`absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b ${p.accent} transition-opacity ${
                          selected ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      {/* Unread dot */}
                      <span
                        aria-hidden
                        className={`mt-1.5 h-2 w-2 flex-none rounded-full ${p.dot} ${
                          selected ? "opacity-100" : "opacity-40"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <h3 className="truncate font-display text-sm sm:text-base text-ink">
                            {p.title}
                          </h3>
                          <span className="flex-none font-mono text-[9px] uppercase tracking-[0.2em] text-ink/60">
                            {p.issue}
                          </span>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-ink/80">{p.body}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Preview pane */}
            <div
              key={active}
              className="animate-fade-in relative flex flex-col p-6 sm:p-8 md:min-h-full motion-reduce:animate-none"
            >
              {/* Soft accent glow behind the open thread */}
              <span
                aria-hidden
                className={`pointer-events-none absolute inset-0 opacity-30 transition-opacity duration-300 bg-gradient-to-br ${current.accent}`}
                style={{
                  filter: "blur(60px) saturate(120%)",
                  transform: "scale(0.85)",
                  mixBlendMode: "multiply",
                }}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-b from-cream via-cream/95 to-cream"
              />

              <div className="relative flex items-start justify-between gap-4 border-b border-ink/10 pb-4">
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">
                    From · Scott Cunningham
                  </p>
                  <h4 className="mt-1 font-display text-lg sm:text-xl">
                    <span
                      className={`bg-gradient-to-r bg-clip-text text-transparent ${current.accent}`}
                    >
                      {current.title}
                    </span>
                  </h4>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                    Monthly · {current.issue}
                  </p>
                </div>
                <div
                  className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-md ${current.accent}`}
                >
                  <Icon className="h-5 w-5" />
                </div>
              </div>
              <div className="relative mt-5 text-sm sm:text-base leading-relaxed text-ink">
                <p className="whitespace-pre-line">{current.emailBody}</p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">
                  Best, Scott
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
